import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { mobileResetPasswordService } from '../services/mobileResetPasswordService';
import { useApiError } from '../../shared/hooks/useApiError';
import { MessageDialog } from '../../shared/ui';

// CSS-in-JS スタイル
const styles = {
  container: {
    minHeight: '100vh',
    background: 'white',
    position: 'relative',
    paddingBottom: '60px'
  },
  logo: {
    display: 'block',
    margin: '20px auto',
    maxWidth: '250px',
    width: '100%',
    height: 'auto'
  },
  title: {
    margin: '20px 10px',
    padding: '6px 0 6px 20px',
    background: '#FDEFEF',
    borderLeft: '6px solid #2B2222'
  },
  titleText: {
    fontSize: '14pt',
    fontWeight: 'bold',
    color: '#2B2222'
  },
  message: {
    padding: '0 10px',
    textAlign: 'left',
    lineHeight: 1.6,
    color: '#333',
    margin: '20px 0'
  },
  inputField: {
    width: '95%',
    display: 'block',
    margin: '14px auto',
    padding: '10px',
    border: '1px solid',
    borderColor: '#ddd',
    borderRadius: '4px',
    fontSize: '16px',
    background: '#ffffff'
  },
  inputFieldFocus: {
    outline: 'none',
    borderColor: '#2B2222',
    background: 'white'
  },
  inputFieldError: {
    borderColor: '#ff6b6b',
    background: '#fff5f5'
  },
  errorMessage: {
    color: '#ff6b6b',
    textAlign: 'center',
    margin: '10px 0',
    fontSize: '14px'
  },
  registButton: {
    width: '90%',
    display: 'block',
    margin: '40px auto',
    padding: '12px 0',
    background: '#2B2222',
    color: '#ffffff',
    border: 'none',
    borderRadius: '24px',
    fontSize: '16px',
    letterSpacing: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  buttonHover: {
    background: '#4a4a4a'
  },
  buttonDisabled: {
    background: '#cccccc',
    cursor: 'not-allowed',
    opacity: 0.7
  },
  formArea: {
    padding: '0 20px',
    maxWidth: '1024px',
    margin: '0 auto'
  },
  messageArea: {
    padding: '0 20px',
    maxWidth: '1024px',
    margin: '0 auto'
  },
  inputContainer: {
    margin: '20px 0'
  },
  buttonContainer: {
    margin: '40px 0'
  }
};

const AppResetPassword = () => {
  const [searchParams] = useSearchParams();
  
  // APIエラーハンドリングフック
  const { 
    showErrorDialog,
    errorMessage,
    handleApiError,
    closeErrorDialog
  } = useApiError(() => setIsLoading(false))
  
  // フォーム状態
  const [password1, setPassword1] = useState(''); // パスワード
  const [password2, setPassword2] = useState(''); // 確認用パスワード
  const [isSubmitted, setIsSubmitted] = useState(false); // 送信済みフラグ
  const [isLoading, setIsLoading] = useState(false); // ローディング状態
  const [error, setError] = useState(''); // エラーメッセージ
  const [resetToken, setResetToken] = useState(''); // リセットトークン
  const [isValidToken, setIsValidToken] = useState(null); // トークン有効性フラグ（null: 未確認, true: 有効, false: 無効）
  const hasCheckedToken = useRef(false); // トークン確認済みフラグ

  // リセットトークン有効性確認処理
  const handleCheckResetPassword = useCallback(async (token) => {
    setIsLoading(true);
    setError('');

    try {
      const result = await mobileResetPasswordService.checkResetPassword(token);

      if(result.returnCode !== '200'){
        setIsValidToken(false);
      }

      if (result.resetable) {
        setIsValidToken(true);
      } else {
        setIsValidToken(false);
      }
    } catch (error) {
      setIsValidToken(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // コンポーネント初期化
  useEffect(() => {
    // 既にトークン確認済みの場合は処理をスキップ
    if (hasCheckedToken.current) {
      return;
    }

    // 複数のパラメータ名に対応（既存コードとの互換性のため）
    const token = searchParams.get('resetToken') 
    
    if (token) {
      setResetToken(token);
      hasCheckedToken.current = true; // 確認済みフラグを設定
      // リセットトークンの有効性を確認
      handleCheckResetPassword(token);
    } else {
      // トークンが存在しない場合は即座に無効として設定
      hasCheckedToken.current = true; // 確認済みフラグを設定
      setIsValidToken(false);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // パスワードバリデーション
  const validatePasswords = () => {
    setError(''); // まずエラーメッセージをクリア
    
    if (!password1.trim() || !password2.trim()) {
      setError('パスワードを入力してください。');
      return false;
    }
    
    if (password1 !== password2) {
      setError('確認用パスワードが一致しません。');
      return false;
    }
    
    if (password1.length < 6) {
      setError('パスワードは6文字以上で入力してください。');
      return false;
    }
    
    return true;
  };

  // Enterキーでの送信処理
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      e.preventDefault();
      handleUpdatePassword();
    }
  };

  // パスワード入力変更処理
  const handlePassword1Change = (e) => {
    setPassword1(e.target.value);
    if (error) setError(''); // エラーメッセージをクリア
  };

  const handlePassword2Change = (e) => {
    setPassword2(e.target.value);
    if (error) setError(''); // エラーメッセージをクリア
  };

  // パスワード更新処理
  const handleUpdatePassword = async () => {
    // バリデーションを実行し、エラーがある場合は処理を停止
    if (!validatePasswords()) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await mobileResetPasswordService.updatePixLabelPassword(resetToken, password1);
      
      // ビジネスロジックエラーのチェック
      if (result.returnCode !== '200') {
        MessageDialog.show('エラー', 'パスワードの更新に失敗しました。', null, 'error');
        return;
      }

      setIsSubmitted(true);
    } catch (error) {
      handleApiError(new Error('パスワードの更新に失敗しました。'));
    } finally {
      setIsLoading(false);
    }
  };


  // ローディング画面表示（トークン確認中のみ）
  if (isValidToken === null) {
    return (
      <div style={styles.container}>
        <div style={styles.messageArea}>
          <img 
            src="/core/img/pix-label-logo.png" 
            alt="pix-label" 
            style={styles.logo}
          />
          <div style={styles.title}>
            <span style={styles.titleText}>確認中</span>
          </div>
          <div style={styles.message}>
            リセットトークンを確認しています...
          </div>
        </div>
      </div>
    );
  }

  // 無効トークンエラー画面表示
  if (isValidToken === false) {
    return (
      <div style={styles.container}>
        <div style={styles.messageArea}>
          <img 
            src="/core/img/pix-label-logo.png" 
            alt="pix-label" 
            style={styles.logo}
          />
          <div style={styles.title}>
            <span style={styles.titleText}>エラー</span>
          </div>
          <div style={styles.message}>
            このリンクは無効です。
          </div>
        </div>
      </div>
    );
  }

  // フォーム入力画面レンダリング
  const renderFormArea = () => (
    <div style={styles.formArea}>
      <img 
        src="/core/img/pix-label-logo.png" 
        alt="pix-label" 
        style={styles.logo}
      />
      <div style={styles.title}>
        <span style={styles.titleText}>パスワード再設定</span>
      </div>
      <div style={styles.message}>
        新しいパスワードを入力してください。
      </div>
      <div style={styles.inputContainer} onKeyDown={handleKeyDown}>
        <input
          type="password"
          value={password1}
          onChange={handlePassword1Change}
          placeholder="パスワード"
          style={{
            ...styles.inputField,
            ...(error ? styles.inputFieldError : {})
          }}
          onFocus={(e) => {
            e.target.style.outline = 'none';
            e.target.style.borderColor = '#2B2222';
            e.target.style.background = 'white';
            if (error) setError(''); // エラーメッセージもクリア
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#ddd';
            e.target.style.background = '#ffffff';
          }}
        />
        <input
          type="password"
          value={password2}
          onChange={handlePassword2Change}
          placeholder="パスワード(確認)"
          style={{
            ...styles.inputField,
            ...(error ? styles.inputFieldError : {})
          }}
          onFocus={(e) => {
            e.target.style.outline = 'none';
            e.target.style.borderColor = '#2B2222';
            e.target.style.background = 'white';
            if (error) setError(''); // エラーメッセージもクリア
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#ddd';
            e.target.style.background = '#ffffff';
          }}
        />
      </div>
      {error && <div style={styles.errorMessage}>{error}</div>}
      <div style={styles.buttonContainer}>
        <button 
          onClick={handleUpdatePassword} 
          disabled={isLoading}
          style={{
            ...styles.registButton,
            ...(isLoading ? styles.buttonDisabled : {})
          }}
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.target.style.background = '#4a4a4a';
            }
          }}
          onMouseLeave={(e) => {
            if (!isLoading) {
              e.target.style.background = '#2B2222';
            }
          }}
        >
          {isLoading ? '更新中...' : '登録'}
        </button>
      </div>
    </div>
  );

  // 更新完了画面レンダリング
  const renderMessageArea = () => (
    <div style={styles.messageArea}>
      <img 
        src="/core/img/pix-label-logo.png" 
        alt="pix-label" 
        style={styles.logo}
      />
      <div style={styles.title}>
        <span style={styles.titleText}>パスワード再設定完了</span>
      </div>
      <div style={styles.message}>
        パスワードが更新されました。
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      {!isSubmitted ? renderFormArea() : renderMessageArea()}

      {/* Error Dialog */}
      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title="メッセージ"
        message={errorMessage}
        type="error"
        data-testid="message-dialog"
      />
    </div>
  );
};

export default AppResetPassword;
